import {
  ServiceOfferingSummaryServiceOffering,
} from '../ServiceOfferingsSummary';
import { formatMoney } from 'Common/functions/Money';
import { ServiceOfferingType } from 'Generated/graphql';
import { getDropoffMessageCopy } from 'Common/functions/dropoff';
import { Select } from 'antd';
import humanizeMinutes from 'Common/utils/humanizeMinutes';
import { UseServiceProviders } from 'Common/hooks/useServiceProviders';
import { useMemo } from 'react';

type serviceOfferingCategories = UseServiceProviders[number]['serviceOfferingCategories']

type ServiceOfferingSelectCategorizedProps = {
  serviceOfferingCategories: serviceOfferingCategories;
  serviceOfferings: ServiceOfferingSummaryServiceOffering[];
  value?: string | null;
  dropoffDeltaDays?: number;
  onSelectServiceOffering?: (so: ServiceOfferingSummaryServiceOffering) => void;
  filter?: (soUid: string) => boolean;
  renderPrice?: (price: number) => React.ReactNode;
  children?: React.ReactNode;
  title?: React.ReactNode;
  additionalClasses?: string;
}

const ServiceOfferingSelectCategorized: React.FC<ServiceOfferingSelectCategorizedProps> = ({
  serviceOfferingCategories,
  serviceOfferings,
  value,
  dropoffDeltaDays,
  onSelectServiceOffering,
  filter,
  renderPrice,
  children,
  title = (
    <label htmlFor="service-type" className="block text-xs text-black uppercase">Services</label>
  ),
  additionalClasses = 'lg:max-w-[50%]',
}) => {
  const allServiceOfferings = useMemo(() => {
    const csos = serviceOfferingCategories.flatMap(
      c => c.serviceOfferings
    );
    return [
      ...serviceOfferings,
      ...csos
    ];
  }, [serviceOfferings.length, serviceOfferingCategories.length]);

  const allUncategorizedServiceOfferings = serviceOfferings.filter(
    s => !serviceOfferingCategories.some(
      c => c.serviceOfferings.some(cs => cs.uid === s.uid),
    ),
  );
  const uncategorizedServiceOfferings = filter ?
    allUncategorizedServiceOfferings.filter(so => filter(so.uid)) :
    allUncategorizedServiceOfferings;

  const renderServiceOffering = (
    so: Pick<ServiceOfferingSummaryServiceOffering, 'uid' | 'basePrice' | 'baseTime' | 'type' | 'title'>,
    keySuffix: string,
  ) => {
    const isPickup =
      so.type === ServiceOfferingType.PickupDropOff;
    let suffix: string | undefined = '';
    if (isPickup && dropoffDeltaDays !== undefined) {
      suffix =
        getDropoffMessageCopy(dropoffDeltaDays);
    }

    const priceFormatted = renderPrice ?
      renderPrice(so.basePrice) :
      formatMoney(so.basePrice);

    return (
      <Select.Option
        key={so.uid + keySuffix}
        value={so.uid}
      >
        <div>
          <div
            className="flex flex-col items-baseline text-lg"
          >
            <span className="overflow-clip line-clamp-1 whitespace-normal">
              {so.title}
            </span>
            <span className="font-bold">
              {priceFormatted ? (
                <>
                  From {priceFormatted}
                </>
              ) : ''}
            </span>
          </div>
          <span>
            {humanizeMinutes(so.baseTime, true)}
            {suffix ? <> / {suffix}</> : null}
          </span>
        </div>
      </Select.Option>
    );
  };

  return (
    <div className="w-full">
      {title}
      <div className="flex gap-4 justify-start sm:flex-col lg:flex-row items-center">
        <Select
          id="service-type"
          className={`mt-1 p-2 text-base focus:outline-none focus:ring-primary sm:text-sm rounded-md basis-1/2 sm:w-full lg:w-2/3 bg-transparent ${additionalClasses}`}
          style={{
            height: 'auto',
          }}
          size="large"
          onChange={s => {
            const so = allServiceOfferings.find(so => so.uid === s);
            if (so) {
              onSelectServiceOffering && onSelectServiceOffering(so);
            }
          }}
          value={value}
        >
          {serviceOfferingCategories?.map(c => {
            const offerings = c.serviceOfferings.filter(
              s => filter ? filter(s.uid) : true
            );

            return offerings.length > 0 ? (
              <Select.OptGroup key={c.uid} label={c.title}>
                {offerings.map(so => renderServiceOffering(so, c.title))}
              </Select.OptGroup>
            ) : <></>;
          })}
          {uncategorizedServiceOfferings?.map(so => renderServiceOffering(so, 'uncat'))}
        </Select>
        {children}
      </div>
    </div>
  );
};

export default ServiceOfferingSelectCategorized;

